import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { type ConversationTextVariant } from '../../components/ConversationText'
import {
  type CatalogItemTagIcon,
  type CatalogItemTagVariant,
  CatalogItemTagVariants,
} from '../../value-objects/CatalogItemTag'
import { type ConversationTelemetryEvent } from '../../value-objects/ConversationTelemetryEvent'
import {
  type ConversationCartAction,
  type ConversationCatalogItemComponent,
  type ConversationCatalogItemDelivery,
  type ConversationCatalogItemTag,
  type ConversationCatalogItemVendorBadge,
  type ConversationComponent,
  type ConversationDetailBlock,
  type ConversationDetailSection,
  deriveDetailBlockKey,
  deriveDetailSectionKey,
  type ConversationEnvelopeItem,
  type ConversationMessageSender,
  type ConversationVendor,
  type ConversationVolumePricing,
  type ConversationVolumePricingTier,
} from '../../value-objects/ConversationTurnItem'
import {
  type CatalogItemVendorBadgeSlotEnum,
  CatalogItemVendorBadgeSlots,
  type ConversationCartActionIconEnum,
  ConversationCartActionIcons,
  ConversationComponentTypes,
  ConversationDetailBlockTypes,
  type ConversationDetailIconEnum,
  ConversationDetailIcons,
  ConversationItemKinds,
} from '../../value-objects/enums'
import {
  type ConversationCartActionApiModel,
  type ConversationCatalogItemComponentApiModel,
  type ConversationCatalogItemDeliveryApiModel,
  type ConversationCatalogItemMoreInformationApiModel,
  type ConversationCatalogItemTagApiModel,
  type ConversationCatalogItemPriceParamsApiModel,
  type ConversationCatalogItemVendorBadgeApiModel,
  type ConversationComponentApiModel,
  type ConversationDetailBlockApiModel,
  type ConversationDetailSectionApiModel,
  type ConversationEnvelopeApiModel,
  type ConversationStreamEventApiModel,
  type ConversationVendorApiModel,
  type ConversationVolumePricingApiModel,
  type ConversationVolumePricingTierApiModel,
} from '../api-models/ConversationEventApiModel'

const normalizeSender = (sender: string): ConversationMessageSender =>
  sender === 'user' ? 'user' : 'llm'

const REDIRECT_PROTOCOL = 'https:'

// The reserved checkout tab starts as about:blank, which inherits this origin,
// so a javascript: or data: payload would execute as Procurify. Only an
// absolute https url is worth handing to it.
const normalizeRedirectUrl = (url?: string): Nullable<string> => {
  const trimmed = url?.trim()
  if (!trimmed) return null
  try {
    return new URL(trimmed).protocol === REDIRECT_PROTOCOL ? trimmed : null
  } catch {
    return null
  }
}

const normalizeImg = (img?: string): string | undefined =>
  img && !img.includes('image_placeholder') ? img : undefined

const DEFAULT_RATING_SCALE_MAX = 5

const isCatalogItemVendorBadgeSlot = (
  slot: string
): slot is CatalogItemVendorBadgeSlotEnum =>
  (Object.values(CatalogItemVendorBadgeSlots) as string[]).includes(slot)

const isConversationCartActionIcon = (
  icon: string
): icon is ConversationCartActionIconEnum =>
  (Object.values(ConversationCartActionIcons) as string[]).includes(icon)

const isCatalogItemTagVariant = (
  variant: string
): variant is CatalogItemTagVariant =>
  (Object.values(CatalogItemTagVariants) as string[]).includes(variant)

const isConversationDetailIcon = (
  icon: string
): icon is ConversationDetailIconEnum =>
  (Object.values(ConversationDetailIcons) as string[]).includes(icon)

const mapVendorBadges = (
  badges: ConversationCatalogItemVendorBadgeApiModel[] = []
): ConversationCatalogItemVendorBadge[] =>
  badges
    .filter((badge) => isCatalogItemVendorBadgeSlot(badge.slot))
    .map((badge) => ({
      slot: badge.slot as CatalogItemVendorBadgeSlotEnum,
      text: badge.text,
      ...(badge.icon && { icon: badge.icon as CatalogItemTagIcon }),
      ...(badge.logo_url && { logoUrl: badge.logo_url }),
    }))

const withDealBadge = (
  badges: ConversationCatalogItemVendorBadge[],
  dealTag?: string
): ConversationCatalogItemVendorBadge[] => {
  if (!dealTag) return badges

  return [
    ...badges.filter(
      (badge) => badge.slot !== CatalogItemVendorBadgeSlots.DEAL
    ),
    { slot: CatalogItemVendorBadgeSlots.DEAL, text: dealTag },
  ]
}

const mapDelivery = (
  delivery?: ConversationCatalogItemDeliveryApiModel
): Nullable<ConversationCatalogItemDelivery> =>
  delivery?.label ? { label: delivery.label } : null

const mapCatalogItemTag = (
  tag: ConversationCatalogItemTagApiModel
): ConversationCatalogItemTag => ({
  ...(tag.icon && { icon: tag.icon as CatalogItemTagIcon }),
  text: tag.text,
  ...(tag.variant &&
    isCatalogItemTagVariant(tag.variant) && { variant: tag.variant }),
})

const mapDetailBlock = (
  block: ConversationDetailBlockApiModel
): Nullable<ConversationDetailBlock> => {
  switch (block.type) {
    case ConversationDetailBlockTypes.TEXT: {
      const content = block.content?.trim()
      if (!content) return null
      return {
        type: ConversationDetailBlockTypes.TEXT,
        content,
        variant: block.variant === 'caption' ? 'caption' : 'body',
      }
    }
    case ConversationDetailBlockTypes.KEY_VALUE: {
      const rows = (block.rows ?? [])
        .map((row) => ({
          label: row.label?.trim() ?? '',
          value: row.value?.trim() ?? '',
        }))
        .filter(
          (row, index, mapped) =>
            row.label &&
            mapped.findIndex(
              (candidate) =>
                candidate.label === row.label && candidate.value === row.value
            ) === index
        )
      if (rows.length === 0) return null
      const collapsedAfter = block.collapsed_after ?? 0
      return {
        type: ConversationDetailBlockTypes.KEY_VALUE,
        rows,
        ...(collapsedAfter > 0 && { collapsedAfter }),
      }
    }
    case ConversationDetailBlockTypes.TABLE: {
      const columns = (block.columns ?? []).map((column) => ({
        ...(column.label && { label: column.label }),
        ...(column.align === 'end' && { align: 'end' as const }),
      }))
      const rows = (block.rows ?? []).filter(
        (row, index, all) =>
          row.length === columns.length &&
          all.findIndex((candidate) => candidate.join() === row.join()) ===
            index
      )
      if (columns.length === 0 || rows.length === 0) return null
      return { type: ConversationDetailBlockTypes.TABLE, columns, rows }
    }
    case ConversationDetailBlockTypes.LIST: {
      const items = (block.items ?? [])
        .map((item) => ({
          icon: item.icon,
          title: item.title?.trim(),
          body: item.body?.trim(),
        }))
        .filter(
          (item, index, mapped) =>
            (item.title || item.body) &&
            mapped.findIndex(
              (candidate) =>
                candidate.title === item.title && candidate.body === item.body
            ) === index
        )
        .map((item) => ({
          ...(item.icon &&
            isConversationDetailIcon(item.icon) && { icon: item.icon }),
          ...(item.title && { title: item.title }),
          ...(item.body && { body: item.body }),
        }))
      if (items.length === 0) return null
      return { type: ConversationDetailBlockTypes.LIST, items }
    }
    case ConversationDetailBlockTypes.CHIPS: {
      const options = (block.options ?? [])
        .map((option) => ({
          label: option.label?.trim() ?? '',
          message: option.message?.trim() ?? '',
        }))
        .filter(
          (option, index, mapped) =>
            option.label &&
            option.message &&
            mapped.findIndex(
              (candidate) => candidate.label === option.label
            ) === index
        )
      if (options.length === 0) return null
      return { type: ConversationDetailBlockTypes.CHIPS, options }
    }
    default:
      return null
  }
}

const mapDetailSection = (
  section: ConversationDetailSectionApiModel
): Nullable<ConversationDetailSection> => {
  const blocks = (section.blocks ?? [])
    .map(mapDetailBlock)
    .filter((block): block is ConversationDetailBlock => block !== null)
    .filter(
      (block, index, mapped) =>
        mapped.findIndex(
          (candidate) =>
            deriveDetailBlockKey(candidate) === deriveDetailBlockKey(block)
        ) === index
    )
  if (blocks.length === 0) return null

  const tags = (section.tags ?? []).map(mapCatalogItemTag)
  return {
    id: section.id ?? '',
    ...(section.title && { title: section.title }),
    ...(section.icon &&
      isConversationDetailIcon(section.icon) && { icon: section.icon }),
    variant: section.variant === 'callout' ? 'callout' : 'default',
    ...(tags.length > 0 && { tags }),
    blocks,
  }
}

const mapDetailSections = (
  sections: ConversationDetailSectionApiModel[] | undefined
): ConversationDetailSection[] =>
  (sections ?? [])
    .map(mapDetailSection)
    .filter((section): section is ConversationDetailSection => Boolean(section))
    .filter(
      (section, index, mapped) =>
        mapped.findIndex(
          (candidate) =>
            deriveDetailSectionKey(candidate) ===
            deriveDetailSectionKey(section)
        ) === index
    )

const SKU_LABEL = 'sku'

const deriveSku = (
  specification: [string, string][],
  detailSections: ConversationDetailSection[]
): string => {
  const specificationSku = specification.find(
    ([label]) => label.toLowerCase() === SKU_LABEL
  )?.[1]
  if (specificationSku) return specificationSku

  for (const section of detailSections) {
    for (const block of section.blocks) {
      if (block.type !== ConversationDetailBlockTypes.KEY_VALUE) continue
      const row = block.rows.find(
        (candidate) => candidate.label.toLowerCase() === SKU_LABEL
      )
      if (row?.value) return row.value
    }
  }
  return ''
}

const mapVolumePricingTier = (
  tier: ConversationVolumePricingTierApiModel
): Nullable<ConversationVolumePricingTier> => {
  if (!tier.label || !tier.unit_price?.price) return null
  return {
    label: tier.label,
    unitPrice: tier.unit_price.price,
    currency: tier.unit_price.currency,
    ...(tier.savings_pct && { savingsPct: tier.savings_pct }),
    ...(tier.min_qty && { minQty: tier.min_qty }),
    ...(tier.max_qty && { maxQty: tier.max_qty }),
  }
}

const mapCurrentPrice = (
  price: ConversationCatalogItemPriceParamsApiModel,
  panelPrice?: ConversationCatalogItemMoreInformationApiModel['price']
): { price: number; currency?: CurrencyName } => {
  if (price.current_price) return price.current_price
  if (price.price !== undefined && price.currency !== undefined) {
    return { price: price.price, currency: price.currency }
  }
  return panelPrice?.current_price ?? { price: 0 }
}

const mapVolumePricing = (
  cardPricing?: ConversationVolumePricingApiModel,
  panelPricing?: ConversationVolumePricingApiModel
): Nullable<ConversationVolumePricing> => {
  // A zeroed or unlabelled hint is the backend's "absent", so each level is
  // validated before it wins — otherwise a card sentinel hides a real panel hint.
  const hint = [cardPricing?.savings_hint, panelPricing?.savings_hint].find(
    (candidate) => candidate?.savings_pct && candidate.label
  )
  const savingsHint =
    hint?.savings_pct && hint.label
      ? {
          savingsPct: hint.savings_pct,
          label: hint.label,
          ...(hint.min_qty && { minQty: hint.min_qty }),
        }
      : null
  // The savings hint rides the card payload while the tier table rides the
  // panel payload; each prefers its home level and falls back to the other.
  const tiers = (
    panelPricing?.tiers?.length
      ? panelPricing.tiers
      : (cardPricing?.tiers ?? [])
  )
    .map(mapVolumePricingTier)
    .filter((tier): tier is ConversationVolumePricingTier => tier !== null)

  if (!savingsHint && tiers.length === 0) return null
  return {
    ...(savingsHint && { savingsHint }),
    ...(tiers.length > 0 && { tiers }),
  }
}

const mapVendor = (
  vendor?: ConversationVendorApiModel
): Nullable<ConversationVendor> =>
  vendor
    ? {
        id: vendor.id,
        name: vendor.name,
        ...(vendor.logo_url && { logoUrl: vendor.logo_url }),
      }
    : null

const mapCartAction = (
  action?: ConversationCartActionApiModel
): Nullable<ConversationCartAction> => {
  if (!action?.text) return null

  const icon = action.icon
  return {
    text: action.text,
    ...(icon && isConversationCartActionIcon(icon) && { icon }),
  }
}

const TEXT_VARIANTS: readonly ConversationTextVariant[] = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
]

const normalizeTextVariant = (variant?: string): ConversationTextVariant =>
  (TEXT_VARIANTS as readonly string[]).includes(variant ?? '')
    ? (variant as ConversationTextVariant)
    : 'h4'

const mapCatalogItemComponent = (
  component: ConversationCatalogItemComponentApiModel
): ConversationCatalogItemComponent => {
  const { title, body, price, tags, rating, delivery } = component.params
  const vendor = mapVendor(component.params.vendor)
  const img = normalizeImg(component.params.img)
  const moreInformation = component.params.more_information
  const vendorBadges = withDealBadge(
    mapVendorBadges(component.params.vendor_badges),
    price.deal_tag || moreInformation?.price?.deal_tag
  )
  const mappedDelivery =
    mapDelivery(delivery) ?? mapDelivery(moreInformation?.delivery)
  const currentPrice = mapCurrentPrice(price, moreInformation?.price)
  const listPrice =
    price.old_price?.price || moreInformation?.price?.old_price?.price || 0
  const volumePricing = mapVolumePricing(
    price.volume_pricing,
    moreInformation?.price?.volume_pricing
  )
  const specification = (moreInformation?.specification ?? []).filter(
    ([label, value], index, pairs) =>
      pairs.findIndex(([l, v]) => l === label && v === value) === index
  )
  const images = Array.from(
    new Set(
      (moreInformation?.images ?? [])
        .map((image) => normalizeImg(image))
        .filter((image): image is string => Boolean(image))
    )
  )
  const detailSections = mapDetailSections(moreInformation?.detail_sections)
  const sku = deriveSku(specification, detailSections)

  return {
    type: ConversationComponentTypes.CATALOG_ITEM,
    itemId: sku,
    ...(img && { img }),
    title,
    body,
    sku,
    price: currentPrice.price,
    ...(currentPrice.currency && { currency: currentPrice.currency }),
    tags: tags.map(mapCatalogItemTag),
    detailSections,
    ...(images.length > 0 && { images }),
    ...(moreInformation?.quantity && {
      quantity: moreInformation.quantity,
    }),
    ...(vendor && { vendor }),
    ...(rating && {
      rating: {
        value: rating.value,
        count: rating.count,
        scaleMax: rating.scale_max || DEFAULT_RATING_SCALE_MAX,
      },
    }),
    ...(vendorBadges.length > 0 && { vendorBadges }),
    ...(mappedDelivery && { delivery: mappedDelivery }),
    ...(volumePricing && { volumePricing }),
    ...(listPrice > 0 && { listPrice }),
  }
}

function mapConversationComponent(
  component: ConversationComponentApiModel
): Nullable<ConversationComponent> {
  switch (component.type) {
    case ConversationComponentTypes.MESSAGE:
      return {
        type: ConversationComponentTypes.MESSAGE,
        sender: normalizeSender(component.params.sender),
        content: component.params.content,
        ...(component.params.tags && { tags: component.params.tags }),
      }
    case ConversationComponentTypes.CONFIRMATION_OPTIONS:
      return {
        type: ConversationComponentTypes.CONFIRMATION_OPTIONS,
        options: component.params.options,
      }
    case ConversationComponentTypes.GROUPED_DROPDOWN:
      return {
        type: ConversationComponentTypes.GROUPED_DROPDOWN,
        groups: component.params.groups.map((group) => ({
          id: group.id,
          name: group.name,
          options: (group.options ?? []).map((option) => ({
            id: option.id,
            name: option.name,
          })),
        })),
        groupLabel: component.params.group_label,
        optionLabel: component.params.option_label ?? null,
        helperText: component.params.helper_text ?? null,
        defaultGroupId: component.params.default_group_id ?? null,
        defaultOptionId: component.params.default_option_id ?? null,
      }
    case ConversationComponentTypes.CART: {
      const cartVendor = mapVendor(component.params.vendor)
      const continueAction = mapCartAction(component.params.continue_action)

      return {
        type: ConversationComponentTypes.CART,
        cartId: component.params.cart_id,
        version: component.params.version,
        totalItems: component.params.total_items,
        total: component.params.estimated_total.price,
        currency: component.params.estimated_total.currency,
        items: component.params.items.map((item) => {
          const img = normalizeImg(item.img)
          return {
            id: item.line_id,
            name: item.title,
            ...(img && { img }),
            quantity: item.number_of_items,
            price: item.price.price,
          }
        }),
        ...(cartVendor && { vendor: cartVendor }),
        ...(component.params.total_label && {
          totalLabel: component.params.total_label,
        }),
        ...(continueAction && { continueAction }),
        ...(component.params.disclaimer && {
          disclaimer: component.params.disclaimer,
        }),
      }
    }
    case ConversationComponentTypes.CATALOG_ITEM:
      return mapCatalogItemComponent(component)
    case ConversationComponentTypes.TEXT:
      return {
        type: ConversationComponentTypes.TEXT,
        variant: normalizeTextVariant(component.params.variant),
        content: component.params.text ?? component.params.content ?? '',
      }
    case ConversationComponentTypes.ORDER_CONFIRMATION:
      return {
        type: ConversationComponentTypes.ORDER_CONFIRMATION,
        vendorRedirectUrl: normalizeRedirectUrl(
          component.params.vendor_redirect_url
        ),
      }
    default:
      return null
  }
}

export const mapConversationEnvelopeEvent = (
  envelope: ConversationEnvelopeApiModel
): Omit<ConversationEnvelopeItem, 'id'> => ({
  kind: ConversationItemKinds.ENVELOPE,
  envelopeId: envelope.id,
  components: envelope.components
    .map(mapConversationComponent)
    .filter(
      (component): component is ConversationComponent => component !== null
    ),
  isAnswered: false,
})

export const mapConversationTelemetryEvent = (
  event: ConversationStreamEventApiModel
): ConversationTelemetryEvent => ({
  type: event.type,
  data: event.data,
  timestamp: event.timestamp ?? null,
})
